// Incident Lab page (#/incidents/<id>) — local practice, opened from the Incident Labs page
// (src/ui/incidents-view.js) or by direct link.
//
// Built from the same parts as a mission page (src/ui/views.js unitView): the LabRuntime, the
// workspace (topology + consoles), the ticket card, the evidence / targets / coach / verify rail
// and the activity widgets, with the same CSS classes so the split layout looks the same. It is a
// separate view so that unitView (curriculum lookups, unlock order, XP, server-verify badges, map
// links) stays exactly as it is for the 98 missions.
//
// What is different here: reset clears this incident only (a new run, a new rolled fault), and the
// progress object is src/core/incidentProgress.js, not the curriculum one. For a signed-in learner
// a solved run is also replayed by the server (Phase 3, src/core/verifiedLabs.js) and the FIRST
// verified solve earns incident XP once (Phase 4, src/core/xp.js incidentXp: 100 XP, −10 per hint,
// never below 50); "Verified" and the XP are shown only after the server's explicit success.
// Admin test mode and offline / guest use earn nothing and say
// "Local practice · not server verified · no XP awarded" (hints are free there).
// A locked incident (its related domain not complete) shows a locked page and creates no lab state.
import { h, confirmModal, toast } from './h.js';
import { LabRuntime } from '../core/runtime.js';
import { createWorkspace } from './workspace.js';
import { ticketCard } from './views.js';
import { findDomain } from '../core/curriculum.js';
import { INCIDENT_DOMAIN, TIERS, findIncident, incidentKey } from '../incidents/index.js';
import { baseXp, hintCost, incidentXp } from '../core/xp.js';

/** Feature label when nothing is sent to the server (admin test mode, no account, no cloud). */
export const LOCAL_LABEL = 'Local practice · not server verified · no XP awarded';
/** Feature label for a signed-in learner (solved runs are sent to the server for replay). */
export const VERIFY_LABEL = `Server-verified · ${baseXp('INCIDENT')} XP for your first verified solve`;
export const featureLabel = (serverVerify) => (serverVerify ? VERIFY_LABEL : LOCAL_LABEL);

/** What the completion panel says for each verification status (src/core/verifiedLabs.js incidentStatusOf). */
export const VERIFY_STATUS_TEXT = {
  verified: '✓ Verified by the RouteZero server',
  checking: 'Solved locally · verifying with the RouteZero server… XP is added once it is verified.',
  pending: 'Solved locally · waiting to be sent to the RouteZero server for verification. XP is added once it is verified.',
  failed: 'Solved locally · the server could not verify this run, so it is not verified and earns no XP. Run it again with a new fault to try again.',
  unavailable: 'Solved locally · server verification is unavailable right now. Your result is kept on this device and will be sent again later; XP is added once it is verified.',
  local: 'Solved locally · not sent for server verification',
};
/** Verified status with the XP this incident earned (once, for its first verified solve). */
export const verifiedText = (xp) => `${VERIFY_STATUS_TEXT.verified} · ${xp} XP earned on your first verified solve. Practice runs after this add no XP.`;

/** "Domain 02 · LAN Switching" for an incident's unlock requirement. */
export function unlockLabel(inc) {
  const d = findDomain(inc.incident?.related);
  return d ? `Domain ${d.number} · ${d.title}` : '';
}
/** Exam tag chips ("Builds toward"). Information only — never a gate. */
export function examTags(inc) {
  const ex = inc.incident?.exam || [];
  return ex.length ? h('span', { class: 'rz-inc-exams', 'data-testid': 'incident-exams' }, h('span', { class: 'rz-inc-k' }, 'Builds toward'), ex.map((e) => h('span', { class: 'rz-inc-exam' }, e))) : null;
}

function lockedIncident(inc) {
  return h('main', { class: 'page' }, h('section', { class: 'panel locked-page', 'data-testid': 'incident-locked' },
    h('h1', {}, '🔒 ', inc.title, ' is locked'),
    h('p', { class: 'muted' }, `Complete ${unlockLabel(inc)} to unlock this incident. Incidents open once you have learned the skills they use.`),
    h('div', { class: 'row' }, h('a', { class: 'btn primary', href: '#/incidents' }, 'All Incident Labs'), h('a', { class: 'btn ghost', href: '#/' }, 'Home'))));
}

function notFound() {
  return h('main', { class: 'page' }, h('section', { class: 'panel locked-page', 'data-testid': 'incident-not-found' },
    h('h1', {}, 'Incident not found'), h('p', { class: 'muted' }, 'This incident does not exist.'), h('a', { class: 'btn primary', href: '#/' }, 'Back to Home')));
}

/**
 * @param progress  incident progress (createIncidentProgress)
 * @param id        incident id
 * @param rerender  re-run the router
 */
export function incidentView(progress, id, rerender, { serverVerify = false, verifyStatus = null, isVerified = () => false } = {}) {
  const inc = findIncident(id);
  if (!inc) return notFound();
  if (!progress.canOpen(INCIDENT_DOMAIN, inc.id)) return lockedIncident(inc);
  const rt = new LabRuntime(INCIDENT_DOMAIN, inc, progress).init();
  const meta = inc.incident || {};

  const stageHost = h('div', { class: 'stage-host' });
  const rail = h('aside', { class: 'rail' });
  const flow = h('ol', { class: 'flow' });
  const statusChip = h('span', { class: 'chip' });
  const completeHost = h('div');
  let ws = null;
  const wsHost = h('div', { class: 'ws-host' });
  const openWs = h('button', { class: 'btn primary wide ws-open-btn', 'data-testid': 'ws-open', 'aria-label': 'Open lab workspace (network diagram and consoles)', onclick: () => setDrawer(true) }, '▶ OPEN LAB WORKSPACE');
  const left = h('div', { class: 'lab-left', 'data-testid': 'lab-left' }, h('div', { class: 'unit-main' }, stageHost), rail, h('div', { class: 'ws-open-wrap' }, openWs));
  function setDrawer(on) {
    page.classList.toggle('ws-open', on);
    if (on) { ws?.el.querySelector('.ws-view.on')?.focus({ preventScroll: true }); } else openWs.focus({ preventScroll: true });
  }
  const resetIncident = async () => {
    if (await confirmModal({ title: 'Reset this incident?', body: `<p>Clears the device configuration, console history and progress of <b>${inc.title}</b> only, and starts a new run with a new fault.</p><p>Missions and other incidents are not affected.</p>`, confirm: 'Reset this incident', danger: true })) {
      progress.resetUnit(INCIDENT_DOMAIN, inc.id); toast('Incident reset'); rerender();
    }
  };

  const page = h('main', { class: 'page unit incident', 'data-incident': inc.id, 'data-testid': 'incident-page' },
    h('section', { class: 'unit-head panel' },
      h('div', { class: 'uh-main' },
        h('div', { class: 'eyebrow' }, `INCIDENT LAB · TIER ${meta.tier || 2} ${(TIERS[meta.tier]?.name || '').toUpperCase()} · ${meta.severity || 'P2'} · ${(meta.relatedLabel || '').toUpperCase()}`),
        h('h1', {}, inc.title),
        h('div', { class: 'rz-inc-tags' }, h('span', { class: 'chip pending', 'data-testid': 'incident-local' }, featureLabel(serverVerify)), examTags(inc)),
        meta.summary ? h('p', { class: 'muted' }, meta.summary) : null,
        h('p', { class: 'objective' }, h('b', {}, 'OBJECTIVE  '), inc.objective)),
      h('div', { class: 'uh-side' },
        statusChip,
        h('a', { class: 'btn ghost small', href: '#/incidents', 'data-testid': 'incident-back' }, '← Incident Labs'),
        h('button', { class: 'btn danger ghost small', 'data-testid': 'reset-incident', onclick: resetIncident }, '↺ RESET THIS INCIDENT')),
      flow),
    completeHost,
    h('div', { class: 'unit-grid' }, left, wsHost));

  function drawFlow() {
    flow.textContent = '';
    const cur = rt.stage?.phase;
    const reached = new Set(rt.stages.slice(0, rt.state.stage + 1).map((s) => s.phase));
    [...new Set(rt.stages.map((s) => s.phase)), 'RESOLVED'].forEach((p) => {
      const done = rt.state.completed || (reached.has(p) && p !== cur);
      flow.append(h('li', { class: `${p === cur && !rt.state.completed ? 'cur' : ''} ${done ? 'done' : ''}` }, p));
    });
    statusChip.className = `chip ${rt.state.completed ? 'complete' : 'in-progress'}`;
    statusChip.textContent = rt.state.completed ? '✓ RESOLVED' : `STAGE ${rt.state.stage + 1} OF ${rt.stages.length}`;
  }

  function drawStage() {
    stageHost.textContent = '';
    const st = rt.stage;
    const card = h('section', { class: 'stage panel', 'data-stage': st.id },
      h('div', { class: 'stage-head' }, h('span', { class: 'phase' }, st.phase), h('h2', {}, st.title)));
    if (st.ticket) card.append(ticketCard(st.ticket, rt));
    const content = typeof st.content === 'function' ? st.content(rt) : st.content;
    if (content) card.append(h('div', { class: 'prose', html: content }));
    for (const w of st.widgets || []) {
      const box = h('div', { class: `widget ${rt.widget(w.id).done ? 'done' : ''}`, 'data-widget': w.id });
      card.append(box);
      w.mount(box, rt, { done: () => { rt.widgetDone(w.id); box.classList.add('done'); drawRail(); }, state: rt.widget(w.id), save: () => rt.save(), refresh: () => { ws?.refresh(); drawRail(); } });
    }
    stageHost.append(card);
    const split = st.workspace !== false && !!inc.network;
    page.classList.toggle('split', split);
    if (!split) { page.classList.remove('ws-expanded', 'ws-open'); wsHost.textContent = ''; }
    if (split) {
      if (!ws) ws = createWorkspace(rt, { onChange: () => drawRail(), onExpand: (on) => page.classList.toggle('ws-expanded', on), onClose: () => setDrawer(false) });
      ws.setDevices();
      if (ws.el.parentNode !== wsHost) wsHost.append(ws.el);
    }
  }

  let lastVerify = null;
  function drawRail() {
    rail.textContent = '';
    const st = rt.stage;
    if (rt.state.completed) {
      rail.append(h('section', { class: 'panel rail-card ok' }, h('div', { class: 'rc-title' }, 'RESOLVED'), h('p', {}, 'This run is resolved. Reset the incident to practice again with a new fault.')));
      return;
    }
    const ev = rt.evidenceStatus();
    if (ev.length) {
      rail.append(h('section', { class: 'panel rail-card', 'data-testid': 'evidence' },
        h('div', { class: 'rc-title' }, 'REQUIRED EVIDENCE', h('span', { class: 'count' }, `${ev.filter((e) => e.done).length}/${ev.length}`)),
        h('ul', { class: 'checks' }, ev.map((e) => h('li', { class: e.done ? 'done' : '' }, h('i'), e.text)))));
    }
    if (st.tasks?.length) {
      const vr = rt.state.verify[st.id];
      rail.append(h('section', { class: 'panel rail-card', 'data-testid': 'targets' },
        h('div', { class: 'rc-title' }, 'VERIFICATION TARGETS'),
        h('ul', { class: 'checks targets' }, st.tasks.map((t) => {
          const r = vr?.results.find((x) => x.id === t.id);
          return h('li', { class: r ? (r.ok ? 'done' : 'fail') : '', 'data-task': t.id }, h('i'), t.text, r && !r.ok && r.fail ? h('div', { class: 'fail-msg' }, r.fail) : null);
        })),
        h('div', { class: 'muted small' }, 'Targets are checked against live device state only when you press Verify.')));
    }
    if (st.question) rail.append(h('section', { class: 'panel rail-card q' }, h('div', { class: 'rc-title' }, 'ENGINEER QUESTION'), h('p', { html: st.question })));
    const hints = st.hints || [];
    const shown = rt.hintsShown();
    const coach = h('section', { class: 'panel rail-card coach' }, h('div', { class: 'rc-title' }, 'INCIDENT COACH'));
    hints.slice(0, shown).forEach((t, i) => coach.append(h('div', { class: 'hint' }, h('b', {}, `Hint ${i + 1}  `), h('span', { html: t }))));
    // hints cost XP only while this run can still earn the incident's XP (a signed-in learner whose
    // incident is not verified yet); local practice and practice after verification are free
    const earning = serverVerify && !isVerified();
    if (earning && shown) coach.append(h('p', { class: 'small muted', 'data-testid': 'hint-xp' }, `${shown} hint${shown === 1 ? '' : 's'} used · this incident now earns ${incidentXp(shown)} of ${baseXp('INCIDENT')} XP`));
    if (shown < hints.length) coach.append(h('button', { class: 'btn ghost small', 'data-testid': 'hint', 'aria-label': `Ask the coach for hint ${shown + 1} of ${hints.length}${earning ? `; costs ${hintCost('INCIDENT')} XP` : ''}`, onclick: () => { rt.revealHint(); drawRail(); } }, `Ask the coach (hint ${shown + 1} of ${hints.length}${earning ? ` · −${hintCost('INCIDENT')} XP` : ''})`));
    if (hints.length) rail.append(coach);
    const b = rt.blockers();
    const box = h('section', { class: 'panel rail-card verify' }, h('div', { class: 'rc-title' }, 'VERIFICATION'));
    if (lastVerify?.blocked) box.append(h('div', { class: 'vmsg warn' }, 'Not yet — collect the required evidence and finish the activity first.'));
    else if (lastVerify && !lastVerify.ok) box.append(h('div', { class: 'vmsg bad', 'data-testid': 'verify-fail' }, 'Verification failed. Observed behaviour still differs from expected. Keep investigating.'));
    const isLast = rt.state.stage === rt.stages.length - 1;
    box.append(h('button', {
      class: `btn primary wide ${b.length ? 'soft' : ''}`, 'data-testid': 'verify',
      onclick: () => { lastVerify = rt.verify(); if (!lastVerify.ok) drawRail(); },
    }, isLast ? 'Close the incident' : 'Verify & continue'));
    if (b.length) box.append(h('div', { class: 'muted small' }, `${b.length} requirement${b.length > 1 ? 's' : ''} outstanding.`));
    rail.append(box);
  }

  function drawComplete() {
    completeHost.textContent = '';
    if (!rt.state.completed) return;
    const r = progress.unitRecord(INCIDENT_DOMAIN, inc.id);
    completeHost.append(h('section', { class: 'panel completion', 'data-testid': 'completion' },
      h('div', { class: 'big-ok' }, '✓'),
      h('div', {},
        h('h2', {}, 'Incident resolved'),
        serverVerify && verifyStatus ? verifyStatus(incidentKey(inc.id))
          : h('p', { class: 'rz-verify', 'data-testid': 'incident-verify-status', 'data-state': 'local' }, VERIFY_STATUS_TEXT.local),
        h('p', { class: 'muted', 'data-testid': 'incident-no-xp' }, `${serverVerify
          ? `Incident XP is added once, when the server verifies your first solve (${baseXp('INCIDENT')} XP, −${hintCost('INCIDENT')} per hint). Incidents do not count toward missions, domain progress, medals, quests, Your week or certificates.`
          : 'Incident Labs do not award XP here and do not count toward missions, quests or certificates.'}${r.solved ? ` Resolved ${r.solved} time${r.solved === 1 ? '' : 's'} on this account.` : ''}`),
        inc.review ? h('div', { class: 'prose', html: inc.review(rt) }) : null,
        h('div', { class: 'row' },
          h('button', { class: 'btn primary', 'data-testid': 'incident-again', onclick: resetIncident }, '↺ Run again with a new fault'),
          h('a', { class: 'btn ghost', href: '#/incidents' }, 'All Incident Labs')))));
  }

  rt.on((type) => {
    if (type === 'event' || type === 'evidence' || type === 'widget') { if (lastVerify?.blocked && type !== 'event') lastVerify = null; drawRail(); if (type === 'event') ws?.drawDiagram(); }
    if (type === 'stage') { lastVerify = null; drawFlow(); drawStage(); drawRail(); left.scrollTop = 0; window.scrollTo({ top: 0, behavior: 'smooth' }); }
    if (type === 'complete') { drawFlow(); drawRail(); drawComplete(); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  });

  // Same split-height measurement as the mission page.
  const grid = page.querySelector('.unit-grid');
  const fitSplit = () => { if (page.isConnected) page.style.setProperty('--split-top', `${Math.round(grid.getBoundingClientRect().top + window.scrollY)}px`); };
  if (typeof window !== 'undefined') {
    const onResize = () => { if (!page.isConnected) { window.removeEventListener('resize', onResize); return; } fitSplit(); };
    window.addEventListener('resize', onResize);
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => { if (!page.isConnected) { ro.disconnect(); return; } fitSplit(); });
      for (const x of [page.querySelector('.unit-head'), completeHost]) if (x) ro.observe(x);
      setTimeout(() => { fitSplit(); for (const x of [document.querySelector('.admin-banner'), document.querySelector('.topbar')]) if (x) ro.observe(x); }, 0);
    }
  }
  drawFlow(); drawStage(); drawRail(); drawComplete();
  page.__rt = rt;
  page.dataset.key = incidentKey(inc.id);
  return page;
}
